import { Suspense, lazy, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { RotateCw, ArrowRight, Loader2, Home as HomeIcon } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const HouseScene = lazy(() => import("@/components/three/HouseScene"));

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isMobile;
}

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000";

export default function Casa3DShowcase() {
  const isMobile = useIsMobile();

  return (
    <section id="casa-3d" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Copy */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <Reveal>
              <Overline>Esplora con noi</Overline>
              <h2 className="font-display font-semibold text-anthracite text-5xl sm:text-6xl leading-[0.95] tracking-tight mt-5">
                La tua prossima <span className="italic text-brand">casa</span>, da ogni angolazione.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-anthracite/70 max-w-md">
                Ruota il modello e osserva ogni dettaglio. Con Casamore ogni immobile viene
                presentato al meglio: acquirenti più coinvolti, vendite più rapide.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/immobili"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold px-7 py-4 transition-all hover:bg-brand-hover hover:scale-[1.03]"
                  data-testid="casa3d-immobili-cta"
                >
                  Esplora gli immobili
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>

              {!isMobile && (
                <p className="mt-6 inline-flex items-center gap-2 text-sm text-anthracite/50">
                  <RotateCw className="h-4 w-4 text-brand" />
                  Trascina per ruotare la casa
                </p>
              )}
            </Reveal>
          </div>

          {/* 3D / fallback */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal delay={0.15}>
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative rounded-[2rem] overflow-hidden aspect-[5/4] bg-gradient-to-br from-accent-subtle to-warm border border-border-subtle shadow-[0_30px_80px_-30px_rgba(15,92,74,0.35)]"
                data-testid="casa3d-canvas-wrap"
              >
                {isMobile ? (
                  <img
                    src={FALLBACK_IMG}
                    alt="Casa moderna"
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="casa3d-fallback-img"
                  />
                ) : (
                  <Suspense
                    fallback={
                      <div className="absolute inset-0 grid place-items-center text-brand" data-testid="casa3d-loader">
                        <span className="inline-flex items-center gap-2 text-sm font-medium">
                          <Loader2 className="h-5 w-5 animate-spin" /> Caricamento modello 3D…
                        </span>
                      </div>
                    }
                  >
                    <HouseScene />
                  </Suspense>
                )}

                <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3.5 py-1.5 text-xs font-semibold text-brand shadow-sm">
                  <HomeIcon className="h-3.5 w-3.5" /> Modello dimostrativo
                </div>
              </motion.div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
